import { Link } from "react-router-dom";
import {
  ListChecks, ChatsCircle, Receipt, Scales, MagicWand, Bell, Gear, Question, ArrowRight, Leaf,
  House, Bank, FileText, ShieldCheck, CheckCircle, Sparkle,
  Camera, MagnifyingGlass, CurrencyDollar, ArrowsClockwise, UploadSimple,
} from "@phosphor-icons/react";

function FlowStep({ n, icon: Icon, title, sub, accent = "#002FA7" }) {
  return (
    <div className="flex-1 min-w-[140px]" data-testid={`deposit-flow-step-${n}`}>
      <div className="bg-white border-2 p-3 h-full" style={{ borderColor: accent }}>
        <div className="flex items-center gap-2 mb-1.5">
          <span
            className="w-5 h-5 flex items-center justify-center text-[10px] font-bold text-white rounded-full"
            style={{ backgroundColor: accent }}
          >{n}</span>
          <Icon size={14} weight="duotone" style={{ color: accent }} />
        </div>
        <div className="font-heading font-semibold text-[12px] leading-tight mb-1">{title}</div>
        <div className="text-[11px] text-[#525252] leading-snug">{sub}</div>
      </div>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="hidden md:flex items-center justify-center text-[#A3A3A3] flex-shrink-0">
      <ArrowRight size={16} weight="bold" />
    </div>
  );
}

function DepositFlowInfographic() {
  return (
    <div className="my-3" data-testid="deposit-flow-infographic">
      <div className="text-[11px] uppercase tracking-wider text-[#525252] mb-2 flex items-center gap-1.5">
        <Sparkle size={10} weight="duotone" className="text-[#002FA7]" />
        The deposit flow at a glance
      </div>
      <div className="flex flex-col md:flex-row gap-2 md:gap-1">
        <FlowStep
          n={1}
          icon={Camera}
          title="Upload remittance"
          sub="Photo, PDF, or paste — drop it on the page."
          accent="#002FA7"
        />
        <FlowArrow />
        <FlowStep
          n={2}
          icon={Sparkle}
          title="Auto-fill"
          sub="I read payer, amount, date, invoice #."
          accent="#7C3AED"
        />
        <FlowArrow />
        <FlowStep
          n={3}
          icon={MagnifyingGlass}
          title="Find matches"
          sub="Pick the right QBO invoice(s) — split if needed."
          accent="#0EA5E9"
        />
        <FlowArrow />
        <FlowStep
          n={4}
          icon={CurrencyDollar}
          title="Apply payment"
          sub="One click posts a Payment in QuickBooks."
          accent="#2CA01C"
        />
        <FlowArrow />
        <FlowStep
          n={5}
          icon={UploadSimple}
          title="Bank statement"
          sub="Drop CSV/Excel/PDF — deposits cleared, QBO Deposit posted."
          accent="#F59E0B"
        />
        <FlowArrow />
        <FlowStep
          n={6}
          icon={CheckCircle}
          title="Cleared"
          sub="Row turns green. Books are caught up."
          accent="#10B981"
        />
      </div>
      <div className="text-[11px] text-[#525252] italic mt-2 leading-relaxed">
        Steps 1–4 happen on the <b>Remittances</b> page · step 5 uses <b>Upload bank statement</b> in the same place · step 6 happens automatically when amounts &amp; invoice numbers line up (within 2 days = auto-clear with no extra clicks).
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, id, children }) {
  return (
    <section id={id} className="surface-card bg-white p-6 scroll-mt-6" data-testid={`help-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
          <Icon size={18} weight="duotone" className="text-white" />
        </div>
        <h2 className="font-heading text-xl font-medium">{title}</h2>
      </div>
      <div className="space-y-2 text-sm leading-relaxed text-[#333]">
        {children}
      </div>
    </section>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="flex-shrink-0 w-6 h-6 bg-[#F5F5F5] text-[#002FA7] font-semibold text-xs flex items-center justify-center rounded-full">{n}</span>
      <span>{children}</span>
    </li>
  );
}

function TocItem({ to, children }) {
  return (
    <a
      href={to}
      className="px-2.5 py-1 text-[12px] border border-[#E5E5E5] hover:border-[#002FA7] hover:text-[#002FA7] transition-all whitespace-nowrap"
    >
      {children}
    </a>
  );
}

export default function Help() {
  return (
    <div data-testid="help-page" className="p-6 sm:p-8 max-w-[1000px]">
      <div className="mb-8">
        <div className="label-tag mb-2 flex items-center gap-2">
          <Question size={12} weight="duotone" /> Quick start
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
          How this portal works.
        </h1>
        <p className="text-[#525252] mt-3 max-w-2xl">
          A complete, page-by-page walkthrough. The owner, bookkeeper, and business administrator all use the same portal — each role only sees the buttons that apply to them.
          Use this page as your reference; every individual page also has its own collapsible "How this works" tucked under the page title.
        </p>
        <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="help-toc">
          <TocItem to="#dashboard">Dashboard</TocItem>
          <TocItem to="#recurring">Recurring &amp; statements</TocItem>
          <TocItem to="#tax-approvals">Tax approvals</TocItem>
          <TocItem to="#inquiries">Inquiries</TocItem>
          <TocItem to="#remittances">Remittances</TocItem>
          <TocItem to="#qbo">QuickBooks Online</TocItem>
          <TocItem to="#bank-statements">Bank statements</TocItem>
          <TocItem to="#files">Files</TocItem>
          <TocItem to="#notifications">Notifications</TocItem>
          <TocItem to="#settings">Settings &amp; roles</TocItem>
          <TocItem to="#month-end">Month-end checklist</TocItem>
        </div>
      </div>

      <div className="space-y-6">
        <Section id="dashboard" icon={House} title="Dashboard">
          <p>The first page you land on. It shows you, at a glance, what the current month looks like for the active company.</p>
          <ul className="space-y-1.5 mt-3 list-disc list-inside">
            <li><b>Month completion %</b> = submitted recurring items ÷ total recurring items.</li>
            <li><b>Monthly close widget</b> appears in the first 7 days of every month and any time close is blocked. It lists the exact things still outstanding (recurring docs, unposted remittances, uncleared deposits, open inquiries) and deep-links to each one. Dismiss it for the session if you don't need it.</li>
            <li><b>Four metric cards</b>: Outstanding items, Open inquiries, Remittances to reconcile, Submitted this period.</li>
            <li><b>Awaiting your response</b> (left): top open inquiries.</li>
            <li><b>Still needed</b> (right): outstanding recurring items, including ones awaiting your tax approval decision.</li>
          </ul>
        </Section>

        <Section id="recurring" icon={ListChecks} title="Recurring &amp; statements">
          <p>The monthly checklist — bank statements, credit-card statements, loan docs, anything you do every month.</p>
          <ol className="space-y-1.5 mt-3">
            <Step n={1}>Pick a month from the top-left dropdown (past 9 months or up to 6 upcoming).</Step>
            <Step n={2}>Hit <b>Upload</b> on a row → drop the PDF/image → the row auto-marks <b>Submitted</b>.</Step>
            <Step n={3}>Doing many at once? Click <b>Smart upload</b> (magic wand, top right). Drop a stack of PDFs — I&apos;ll identify the vendor on each one and auto-attach it to the matching recurring item. Anything I can&apos;t identify shows up in a popup so you can pick the right item manually.</Step>
            <Step n={4}>Submitted in error? Click the reset arrow on a Submitted row to send it back to Pending.</Step>
            <Step n={5}>The summary cards (Total / Submitted / Awaiting owner / Pending) always reflect the selected month.</Step>
          </ol>
          <p className="mt-3 text-[12px] text-[#525252] italic">
            Owners manage the list of recurring items in <b>Settings → Recurring templates</b>.
          </p>
        </Section>

        <Section id="tax-approvals" icon={Scales} title="Tax approvals (DOR &amp; L&amp;I)">
          <p>These rows have a special two-step flow because the bookkeeper has to calculate the tax first.</p>
          <ol className="space-y-1.5 mt-3">
            <Step n={1}><b>Bookkeeper</b>: close the books, then click <b>Request approval</b> on the DOR or L&amp;I row. Enter the calculated tax amount + an optional note. Status flips to <b>Awaiting owner</b>.</Step>
            <Step n={2}><b>Owner</b>: get a notification (bell icon); open the row and tap <b>Pay Now</b> or <b>Do Not Pay</b>.</Step>
            <Step n={3}><b>Bookkeeper</b> (if Pay Now): pay on the tax portal, copy the whole confirmation page (Ctrl+A → Ctrl+C). Back here, click <b>Paste payment confirmation</b> and paste it in. The receipt is archived with the month.</Step>
          </ol>
          <p className="mt-3 text-[12px] text-[#525252] italic">
            Owner can re-view the receipt anytime by clicking the green <b>Payment receipt</b> badge on the row.
          </p>
        </Section>

        <Section id="inquiries" icon={ChatsCircle} title="Inquiries (the Q&amp;A board)">
          <p>Replaces the email chain about "what was this charge?"</p>
          <ol className="space-y-1.5 mt-3">
            <Step n={1}><b>Bookkeeper</b>: click <b>New inquiry</b>. Be specific in the subject — date, amount, check #, vendor.</Step>
            <Step n={2}>Optional: set <b>Needed by</b> so the owner sees a deadline pill. Overdue items go red automatically.</Step>
            <Step n={3}><b>Owner</b>: the sidebar Inquiries link turns red when there are unresolved items. Open one, type into <b>Submit response</b> — that auto-resolves it.</Step>
            <Step n={4}>Need clarification before resolving? Use the <b>Comment</b> box — comments don't close the question; they continue the thread.</Step>
            <Step n={5}>Reopen any resolved inquiry from the <b>Resolved</b> tab if it comes up again.</Step>
          </ol>
        </Section>

        <Section id="remittances" icon={Receipt} title="Remittances (payment advices)">
          <p>For logging customer payments / checks that need to be matched to invoices in QuickBooks.</p>
          <DepositFlowInfographic />
          <ol className="space-y-1.5 mt-3">
            <Step n={1}><b>Upload</b>: phone camera, file picker, drag-drop on desktop, or paste a screenshot with <span className="font-mono font-bold">⌘V</span> / <span className="font-mono font-bold">Ctrl+V</span>. Multiple files at once OK.</Step>
            <Step n={2}><b>Auto-fill</b>: I read the payer, amount, date, and invoice number from each upload and fill them in for you. Status starts at <b>Pending</b>.</Step>
            <Step n={3}>Click any row to expand. Use the pencil to fix anything that&apos;s wrong. Then click <b>Find matches</b>.</Step>
            <Step n={4}><b>Match in QuickBooks</b>: I pull every open invoice and rank them by amount + payer name + date. Invoices whose number matches the remittance are pinned at the top with a 100% match badge.</Step>
            <Step n={5}><b>Split across invoices</b>: if one payment covers several invoices, tick each invoice and edit the dollar amount per line. The &ldquo;$ Allocated / $ Total&rdquo; tracker turns green when the math is right.</Step>
            <Step n={6}>Click <b>Apply payment to QBO</b>. One Payment is posted in QuickBooks linking all the invoices you selected. The row flips to <b>Applied</b>.</Step>
            <Step n={7}>Use the filter chips at the top (<b>Open / Pending / Deposited / In Undeposited Funds / Needs review / Cleared / All</b>) and the search box (payer, amount, invoice number, date) to find specific rows.</Step>
          </ol>
          <p className="mt-3 text-[12px] text-[#525252] italic">
            Status legend — <b>Pending</b>: just uploaded · <b>Deposited</b>: check is in the bank · <b>Applied</b>: posted to QBO · <b>Cleared</b>: matched a deposit · <b>Overdue</b>: applied 7+ days ago and not yet cleared.
          </p>
          <p className="mt-2 text-[12px] text-[#525252]">
            <b>Bookkeeper review:</b> the portal auto-flags ambiguous remittances (low confidence, multiple plausible customers, amount that doesn&apos;t line up). Flagged rows are <b>handled exclusively by the bookkeeper or owner</b> — they never come back to the business administrator. Anyone can also manually flag a row at any time via the small &ldquo;Flag for bookkeeper review&rdquo; link.
          </p>
        </Section>

        <Section id="qbo" icon={Bank} title="QuickBooks Online connection">
          <p>One-time owner-only setup that powers the matching, applying, and clearance features.</p>
          <ol className="space-y-1.5 mt-3">
            <Step n={1}><b>Owner</b>: go to <Link to="/settings" className="underline text-[#002FA7]">Settings</Link> → QuickBooks Online card → click <b>Connect to QuickBooks</b>. Sign in to Intuit, pick the company, and approve.</Step>
            <Step n={2}>The portal stores a per-company encrypted token. We auto-refresh tokens, so you'll only need to re-connect every 100 days or if Intuit revokes access.</Step>
            <Step n={3}>You can <b>Disconnect</b> from the same card any time. Disconnecting only removes the integration; nothing in your QBO data is touched.</Step>
          </ol>
          <p className="mt-3 text-[12px] text-[#525252] italic">
            Each entity (North Star, Todd's Nursery) connects to its own QBO realm independently.
          </p>
        </Section>

        <Section id="bank-statements" icon={ShieldCheck} title="Bank statement upload &amp; auto-clearance">
          <p>Used to clear Applied remittances when the QBO bank feed isn't synced yet, or when you want to batch-process a month of deposits. Available to anyone on the team.</p>
          <ol className="space-y-1.5 mt-3">
            <Step n={1}>On the Remittances page, click <b>Upload bank statement</b>. Drop a CSV (any common bank export) or PDF.</Step>
            <Step n={2}>I read every credit/deposit line on the statement (7+ date formats supported; PDFs are read line-by-line).</Step>
            <Step n={3}>I match each deposit to one or more Applied remittances. If two or three smaller payments add up to a single bank deposit, I&apos;ll suggest grouping them.</Step>
            <Step n={4}>Pick a <b>QuickBooks bank account</b> from the dropdown (the portal remembers your last choice). Tick <b>Create QuickBooks Deposit transactions</b> if you also want a Deposit posted in QBO — this pulls the Payments out of Undeposited Funds and into the bank register.</Step>
            <Step n={5}>Click confirm. Every matched remittance is marked <b>Cleared</b>. If any QBO Deposit posting fails, you&apos;ll see the reason inline so you can fix and retry.</Step>
          </ol>
          <p className="mt-3 text-[12px] text-[#525252] italic">
            <b>Auto-fire rule:</b> if a bank deposit&apos;s description contains the invoice number, the amount matches exactly, only one customer is involved, and the bank statement is uploaded within 2 days of the original payment — the row clears and a Deposit is posted <b>without any extra clicks</b>. Look for the green &ldquo;Cleared&rdquo; badge.
          </p>
          <p className="mt-2 text-[12px] text-[#525252]">
            The <b>Sync bank clearance</b> button (next to Upload bank statement) checks QuickBooks directly for any Payments that already cleared via your live bank feed. It runs automatically once per session.
          </p>
        </Section>

        <Section id="files" icon={FileText} title="Files">
          <p>The library of every document ever uploaded for this company.</p>
          <ul className="space-y-1.5 mt-3 list-disc list-inside">
            <li>Files arrive here by being uploaded on Recurring, Remittances, or Inquiries — there's no upload button on the Files page itself.</li>
            <li>Use the filter dropdown to see only Statements, only Remittances, or <b>Unlinked only</b>.</li>
            <li>Eye = preview, Link = attach an unlinked file to a recurring item retroactively, Trash = delete (also resets any item the file was marking complete).</li>
            <li>Bookkeeper sweep at month-end: filter <b>Unlinked only</b> to make sure nothing slipped through.</li>
          </ul>
        </Section>

        <Section id="notifications" icon={Bell} title="Notifications, search &amp; navigation">
          <ul className="space-y-1.5 mt-3 list-disc list-inside">
            <li>The <b>bell</b> in the top-right shows every recent action. Red badge = unread.</li>
            <li><b>Inquiries</b> sidebar item turns <span className="text-[#DC2626] font-semibold">red</span> when unresolved exist.</li>
            <li><b>Search</b> (top-right, or ⌘K) finds an item, vendor, inquiry, or remittance across both companies.</li>
            <li>Switch entities (North Star ↔ Todd's) from the sidebar selector. Every page follows the active entity — checklists, inquiries, and remittances are isolated per company.</li>
          </ul>
        </Section>

        <Section id="settings" icon={Gear} title="Settings &amp; roles">
          <ul className="space-y-1.5 mt-3 list-disc list-inside">
            <li><b>Owner</b>: invite team members, create new entities, edit recurring templates, connect QBO, change global preferences.</li>
            <li><b>Bookkeeper</b>: upload statements, request tax approvals, ask inquiries, paste payment confirmations, manage remittances, upload bank statements (with QBO connected).</li>
            <li><b>Business administrator</b> (staff): same as bookkeeper for daily tasks <i>except</i> can&apos;t request tax approvals (that&apos;s bookkeeper-only) and can&apos;t approve Pay Now / Do Not Pay (that&apos;s owner-only).</li>
            <li><b>Everyone</b>: change password in Account &amp; security.</li>
            <li>Sessions expire after <b>1 hour of inactivity</b> for security.</li>
          </ul>
        </Section>

        <Section id="month-end" icon={CheckCircle} title="Month-end close — recommended order">
          <ol className="space-y-1.5 mt-3">
            <Step n={1}><b>Bookkeeper</b>: ensure every row on Recurring is <b>Submitted</b> (use Smart upload to bulk-process). Use the Files page → Unlinked only as a final sweep.</Step>
            <Step n={2}><b>Bookkeeper</b>: process every remittance — find matches, apply to QBO, then upload the bank statement to clear the deposits. The Monthly Close widget on the Dashboard shows a count of any still outstanding.</Step>
            <Step n={3}><b>Bookkeeper</b>: request tax approvals (DOR/L&amp;I) once the books are closed.</Step>
            <Step n={4}><b>Owner</b>: answer all open inquiries, approve/decline tax requests.</Step>
            <Step n={5}><b>Bookkeeper</b>: log tax payment confirmations once paid. Verify the Monthly Close widget says <b>Ready to close</b> in green.</Step>
          </ol>
        </Section>

        <div className="p-5 bg-[#002FA7]/5 border-l-2 border-[#002FA7]">
          <div className="font-heading font-semibold mb-1 flex items-center gap-2">
            <Sparkle size={14} weight="duotone" className="text-[#002FA7]" />
            Still stuck?
          </div>
          <p className="text-sm text-[#333]">
            Open an inquiry — everything stays tied to the company and the month, and the owner gets notified.
          </p>
          <Link
            to="/questions"
            data-testid="help-cta-inquiry"
            className="mt-3 inline-flex items-center gap-2 btn-ikb px-4 py-2 text-sm"
          >
            Open Inquiries <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="flex items-center justify-center gap-2 text-[11px] text-[#a3a3a3] pt-2">
          <Leaf size={12} weight="duotone" /> Todd&apos;s Nursery bookkeeping portal
        </div>
      </div>
    </div>
  );
}
